// Standings & tie-breaking.
//
// Players in a pool are ranked by the chain: Matches Won -> Games Difference ->
// Points Difference (all descending). A fully-deterministic final tie-break on
// player number keeps ordering stable regardless of engine sort stability.

import type { Cell, PlayerId, SportId, Standing, TournamentState } from './types';
import { summarizeMatch } from './scoring';
import { computeFootballStandings } from './football';

function emptyStanding(playerId: PlayerId): Standing {
  return {
    playerId,
    matchesPlayed: 0,
    matchesWon: 0,
    gamesWon: 0,
    gamesLost: 0,
    gamesDiff: 0,
    pointsWon: 0,
    pointsLost: 0,
    pointsDiff: 0,
    rank: 0,
  };
}

function numericId(playerId: PlayerId): number {
  const match = /(\d+)/.exec(playerId);
  return match ? Number(match[1]) : Number.MAX_SAFE_INTEGER;
}

/** Tie-break comparator: Matches Won -> Games Diff -> Points Diff -> player no. */
export function compareStandings(a: Standing, b: Standing): number {
  if (b.matchesWon !== a.matchesWon) return b.matchesWon - a.matchesWon;
  if (b.gamesDiff !== a.gamesDiff) return b.gamesDiff - a.gamesDiff;
  if (b.pointsDiff !== a.pointsDiff) return b.pointsDiff - a.pointsDiff;
  return numericId(a.playerId) - numericId(b.playerId);
}

/** True when every cell in the pool has been completed. */
export function isPoolComplete(cells: readonly Cell[]): boolean {
  return cells.length > 0 && cells.every((cell) => cell.status === 'COMPLETED');
}

/**
 * Computes ranked standings for a pool. Only COMPLETED cells contribute to the
 * tallies; games won, points and the match winner are derived from each cell's
 * per-game scores via `summarizeMatch`.
 */
export function computeStandings(
  playerIds: readonly PlayerId[],
  cells: readonly Cell[],
): Standing[] {
  const table = new Map<PlayerId, Standing>();
  for (const pid of playerIds) {
    table.set(pid, emptyStanding(pid));
  }

  for (const cell of cells) {
    if (cell.status !== 'COMPLETED') continue;
    const a = table.get(cell.playerA);
    const b = table.get(cell.playerB);
    if (!a || !b) continue;

    const summary = summarizeMatch(cell.games, cell.bestOf);

    a.matchesPlayed += 1;
    b.matchesPlayed += 1;

    a.gamesWon += summary.gamesA;
    a.gamesLost += summary.gamesB;
    b.gamesWon += summary.gamesB;
    b.gamesLost += summary.gamesA;

    a.pointsWon += summary.pointsA;
    a.pointsLost += summary.pointsB;
    b.pointsWon += summary.pointsB;
    b.pointsLost += summary.pointsA;

    if (summary.winner === 'A') a.matchesWon += 1;
    else if (summary.winner === 'B') b.matchesWon += 1;
  }

  const standings = [...table.values()];
  for (const s of standings) {
    s.gamesDiff = s.gamesWon - s.gamesLost;
    s.pointsDiff = s.pointsWon - s.pointsLost;
  }
  standings.sort(compareStandings);
  standings.forEach((s, index) => {
    s.rank = index + 1;
  });
  return standings;
}

/**
 * Computes a single tournament-wide ranking by aggregating every group-stage
 * cell across all pools into one ranked standings table. Only the round-robin
 * group stage contributes (bracket play is single-elimination and not
 * points-comparable), and the same Matches Won -> Games Difference -> Points
 * Difference tie-break is applied. The returned `rank` is 1-based across the
 * whole field.
 */
export function computeOverallStandings(
  tournament: TournamentState,
): Standing[] {
  const playerIds = Object.keys(tournament.players);
  const cells = Object.values(tournament.cells);
  return computeStandings(playerIds, cells);
}

/**
 * A ranked pool entry, sport-agnostic: the shared shape of `Standing` and
 * `FootballStanding` that the qualification cascade needs (who is 1st/2nd).
 */
export interface PoolRankEntry {
  playerId: PlayerId;
  rank: number;
}

/**
 * Ranks a pool under the tournament's sport: table tennis by Matches Won ->
 * Games Diff -> Points Diff, football by FIFA 3-1-0 Points -> Goal difference
 * -> Goals scored. The single entry point for slot promotion, withdrawal
 * reconciliation and the podium fallback, so every consumer agrees on the
 * ranking rules per sport.
 */
export function rankPool(
  sportId: SportId,
  playerIds: readonly PlayerId[],
  cells: readonly Cell[],
): PoolRankEntry[] {
  return sportId === 'FOOTBALL'
    ? computeFootballStandings(playerIds, cells)
    : computeStandings(playerIds, cells);
}

/** Tournament-wide ranking under the tournament's sport (see `rankPool`). */
export function rankOverall(tournament: TournamentState): PoolRankEntry[] {
  return rankPool(
    tournament.sportId,
    Object.keys(tournament.players),
    Object.values(tournament.cells),
  );
}
